import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Info } from "lucide-react";

export default function ReviewStep({
  mode,
  person,
  person2,
  family,
  assets,
  distribution,
  executor,
  guardian,
  specialInstructions,
  checks,
  percent,
  onJump,
}) {
  return (
    <div data-testid="step-review">
      <h2 className="font-serif text-2xl text-navy">Final Validation</h2>
      <p className="mt-2 text-sm text-[#4A5568] sm:text-base">
        The system has cross-checked every constraint. Fix any red items — then generate your
        dispute-proof will.
      </p>

      <div
        className="mt-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50/70 p-4 text-xs text-amber-900 sm:text-sm"
        data-testid="review-careful-review-notice"
      >
        <Info size={16} className="mt-0.5 shrink-0 text-amber-700" />
        <span>
          Please review all names, spellings, and distributions carefully before
          generating your final document.
        </span>
      </div>

      <div
        className="mt-4 grid gap-2 rounded-md border border-[#0A1128]/10 bg-[#F9FAFB] p-4 text-sm text-navy sm:grid-cols-2"
        data-testid="review-structured-checklist"
      >
        <div className="flex items-center gap-2">
          <span className="text-emerald-600">✔</span> Personal details completed
        </div>
        <div className="flex items-center gap-2">
          <span className="text-emerald-600">✔</span> Beneficiaries added
        </div>
        <div className="flex items-center gap-2">
          <span className="text-emerald-600">✔</span> Executor appointed
        </div>
        <div className="flex items-center gap-2">
          <span className="text-emerald-600">✔</span> Distribution structured
        </div>
      </div>

      {/* Completion gauge */}
      <div className="mt-6 rounded-xl border border-gold/40 bg-[#FBF7EE] p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
              Will completion
            </div>
            <div className="mt-1 font-serif text-3xl text-navy">{percent}%</div>
          </div>
          <div className="text-right text-sm text-[#4A5568]">
            {checks.filter((c) => c.ok).length} of {checks.length} checks passed
          </div>
        </div>
        <div className="mt-4 h-2 w-full rounded-full bg-white">
          <div
            className="h-full rounded-full bg-gold transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>

        <ul className="mt-5 space-y-2" data-testid="review-checklist">
          {checks.map((c) => (
            <li key={c.id} className="flex items-center gap-3 text-sm">
              {c.ok ? (
                <CheckCircle2 size={18} className="text-emerald-600" />
              ) : (
                <Circle size={18} className="text-red-500" />
              )}
              <span className={c.ok ? "text-[#0A1128]" : "text-red-700"}>{c.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Summary cards */}
      <div className="mt-8 space-y-4">
        <SummaryBlock
          title={mode === "joint" ? "Testator 1" : "Personal Details"}
          onEdit={() => onJump(1)}
          testId="review-personal"
        >
          <PersonSummary p={person} />
        </SummaryBlock>
        {mode === "joint" && (
          <SummaryBlock title="Testator 2" onEdit={() => onJump(1)} testId="review-personal-2">
            <PersonSummary p={person2} />
          </SummaryBlock>
        )}

        <SummaryBlock title={`Family (${family.length})`} onEdit={() => onJump(2)} testId="review-family">
          <ul className="space-y-1 text-sm">
            {family.map((m) => (
              <li key={m.id}>
                <span className="font-medium text-navy">{m.name}</span> — {m.relationship}, age {m.age}
                {Number(m.age) < 18 && m.age !== "" && (
                  <span className="ml-2 text-xs text-gold">(minor)</span>
                )}
              </li>
            ))}
          </ul>
        </SummaryBlock>

        <SummaryBlock title={`Assets (${assets.length})`} onEdit={() => onJump(3)} testId="review-assets">
          <ul className="space-y-1 text-sm">
            {assets.map((a) => (
              <li key={a.id}>
                <span className="font-medium text-navy">{a.type}:</span> {a.description}
                {a.value && ` (₹ ${a.value})`}
              </li>
            ))}
          </ul>
        </SummaryBlock>

        <SummaryBlock
          title={`Distribution — ${distribution.mode === "percentage" ? "By Percentage" : "Asset-wise"}`}
          onEdit={() => onJump(4)}
          testId="review-distribution"
        >
          {distribution.mode === "percentage" ? (
            <ul className="space-y-1 text-sm">
              {distribution.percentages.map((p) => (
                <li key={p.id || p.beneficiary_name}>
                  <span className="font-medium text-navy">{p.beneficiary_name}</span>
                  {p.beneficiary_relationship && <> ({p.beneficiary_relationship})</>} — {p.share}%
                </li>
              ))}
            </ul>
          ) : (
            <ul className="space-y-1 text-sm">
              {distribution.asset_assignments.map((a) => (
                <li key={a.asset_id}>
                  <span className="font-medium text-navy">{a.asset_description}</span> →{" "}
                  {a.beneficiaries.length > 0 ? a.beneficiaries.join(", ") : "— unassigned —"}
                </li>
              ))}
            </ul>
          )}
        </SummaryBlock>

        <SummaryBlock title="Executor" onEdit={() => onJump(5)} testId="review-executor">
          <div className="text-sm">
            <div>
              <span className="font-medium text-navy">{executor.name}</span>
              {executor.relationship && <> — {executor.relationship}</>}
            </div>
            <div className="text-[#4A5568]">{executor.phone}</div>
            {executor.backup_name && (
              <div className="mt-2 text-[#4A5568]">
                Backup: <span className="font-medium text-navy">{executor.backup_name}</span>
                {executor.backup_relationship && <> — {executor.backup_relationship}</>}
                {executor.backup_phone && <>, {executor.backup_phone}</>}
              </div>
            )}
          </div>
        </SummaryBlock>

        {guardian && (
          <SummaryBlock title="Guardian" onEdit={() => onJump(6)} testId="review-guardian">
            <div className="text-sm">
              <div>
                <span className="font-medium text-navy">{guardian.name}</span>
                {guardian.relationship && <> — {guardian.relationship}</>}
              </div>
              {guardian.backup_name && (
                <div className="mt-2 text-[#4A5568]">
                  Backup: <span className="font-medium text-navy">{guardian.backup_name}</span>
                  {guardian.backup_relationship && <> — {guardian.backup_relationship}</>}
                </div>
              )}
            </div>
          </SummaryBlock>
        )}

        {specialInstructions?.trim() && (
          <SummaryBlock title="Special Instructions" onEdit={() => onJump(7)} testId="review-instructions">
            <div className="whitespace-pre-line text-sm text-[#0A1128]/90">
              {specialInstructions}
            </div>
          </SummaryBlock>
        )}
      </div>

      <div className="mt-8 flex items-start gap-3 rounded-md border border-navy/15 bg-[#F9FAFB] p-5 text-sm text-[#4A5568]">
        <Info size={18} className="mt-0.5 shrink-0 text-navy" />
        <div>
          On generation, the following clause is <span className="font-semibold text-navy">automatically inserted</span>:{" "}
          <em>"This Will revokes all previous Wills and Codicils made by me."</em>
        </div>
      </div>
    </div>
  );
}

function SummaryBlock({ title, onEdit, children, testId }) {
  return (
    <div className="rounded-md border border-[#0A1128]/10 p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="font-serif text-lg text-navy">{title}</div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onEdit}
          className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
          data-testid={`${testId}-edit`}
        >
          Edit
        </Button>
      </div>
      <div className="mt-3 text-[#4A5568]">{children}</div>
    </div>
  );
}

function PersonSummary({ p }) {
  return (
    <div className="text-sm">
      <div><span className="font-medium text-navy">Name:</span> {p.full_name || "—"}</div>
      <div><span className="font-medium text-navy">DOB:</span> {p.date_of_birth || "—"}</div>
      <div><span className="font-medium text-navy">Religion:</span> {p.religion || "—"}</div>
      <div><span className="font-medium text-navy">Marital Status:</span> {p.marital_status || "—"}</div>
      <div className="whitespace-pre-line">
        <span className="font-medium text-navy">Address:</span> {p.address || "—"}
      </div>
      <div>
        <span className="font-medium text-navy">Sound-mind declaration:</span>{" "}
        {p.sound_mind ? "Yes" : "— not confirmed —"}
      </div>
    </div>
  );
}
